import type { SVGProps } from "react";
import { AssetWatchStackedLogo } from "./assetwatch-stacked-logo";

export type AssetWatchLogoSize = "default" | "compact" | "display" | "stacked";

const logoToneMap = {
  default: "fill-surface-inverse-surface",
  primary: "fill-primary",
} as const;

export type AssetWatchLogoTone = keyof typeof logoToneMap;

export interface AssetWatchLogoProps
  extends Omit<
    SVGProps<SVGSVGElement>,
    "children" | "className" | "color" | "height" | "ref" | "width"
  > {
  /** Logo size. Defaults to "default" (44px x 23.21px). */
  size?: AssetWatchLogoSize;
  /** Logo color tone. Defaults to the current color for each logo size. */
  tone?: AssetWatchLogoTone;
}

const logoSizeMap: Record<
  AssetWatchLogoSize,
  { width: number; height: number }
> = {
  default: { width: 44, height: 23.21 },
  compact: { width: 24.16, height: 12.74 },
  display: { width: 90, height: 47.47 },
  stacked: { width: 200, height: 87 },
};

/**
 * AssetWatch brand mark.
 *
 * By default the logo is decorative (aria-hidden="true").
 * Provide aria-label when the logo conveys brand identity in context.
 *
 * @example
 * <AssetWatchLogo />
 * <AssetWatchLogo size="compact" aria-label="AssetWatch" />
 */
function AssetWatchLogo({
  size = "default",
  style,
  tone,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden,
  ...props
}: AssetWatchLogoProps) {
  const isDecorative = !ariaLabel;
  const dimensions = logoSizeMap[size];
  const logoTone = tone ?? "primary";

  if (size === "stacked") {
    return (
      <AssetWatchStackedLogo
        {...props}
        className={logoToneMap[logoTone]}
        aria-hidden={ariaHidden ?? (isDecorative ? true : undefined)}
        aria-label={ariaLabel}
        role={isDecorative ? undefined : "img"}
        style={{ flexShrink: 0, ...style }}
      />
    );
  }

  return (
    <svg
      {...props}
      width={dimensions.width}
      height={dimensions.height}
      viewBox="0 0 44 23.21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden ?? (isDecorative ? true : undefined)}
      aria-label={ariaLabel}
      role={isDecorative ? undefined : "img"}
      style={{ flexShrink: 0, ...style }}
    >
      <path
        d="M10.3174 20.54C9.85932 21.349 9.19472 22.0221 8.3916 22.4902C7.58855 22.9583 6.67561 23.2051 5.74609 23.2051H0L4.35254 15.5615H13.1367L10.3174 20.54ZM26.4014 23.2051H20.6514C19.7228 23.2052 18.8103 22.9593 18.0078 22.4922C17.2054 22.0251 16.5415 21.3532 16.083 20.5459L10.2764 10.3164C9.8266 9.52465 9.59036 8.62933 9.59082 7.71875C9.59128 6.80816 9.82872 5.91337 10.2793 5.12207L13.1934 0L26.4014 23.2051ZM39.6104 23.2021H33.8613C32.9331 23.2021 32.021 22.9562 31.2188 22.4893C30.4164 22.0221 29.7514 21.3504 29.293 20.543L23.4863 10.3135C23.0366 9.52227 22.7998 8.62785 22.7998 7.71777C22.7998 6.8077 23.0366 5.91328 23.4863 5.12207L26.4014 0L39.6104 23.2021ZM44 7.71973L39.6084 15.4395L36.6934 10.3174C36.243 9.52576 36.0068 8.63047 36.0068 7.71973C36.0068 6.80898 36.243 5.91369 36.6934 5.12207L39.6084 0L44 7.71973Z"
        className={logoToneMap[logoTone]}
      />
    </svg>
  );
}

export { AssetWatchLogo };
